import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Container from '@mui/material/Container'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import StorefrontIcon from '@mui/icons-material/Storefront'
import type { SharedProps } from '@/types'

interface Props {
  children: React.ReactNode
  title?: string
  noContainer?: boolean
}

export default function AppLayout({ children, title, noContainer }: Props) {
  const { props } = usePage<SharedProps>()
  const { current_user, flash } = props
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [flashOpen, setFlashOpen] = useState(true)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleSignOut = () => {
    handleMenuClose()
    router.delete('/users/sign_out')
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #F5E6E0 0%, #EDD5CC 50%, #E8C9BE 100%)',
          color: 'text.primary',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <IconButton
              edge="start"
              onClick={() => router.visit('/')}
              sx={{ mr: 1, color: 'primary.dark' }}
            >
              <StorefrontIcon />
            </IconButton>
            <Typography
              variant="h6"
              component="a"
              href="/"
              onClick={(e) => { e.preventDefault(); router.visit('/') }}
              sx={{
                flexGrow: 1,
                color: 'primary.dark',
                textDecoration: 'none',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Okonomi App Store
            </Typography>

            {current_user ? (
              <>
                <Button
                  onClick={() => router.visit('/dashboard')}
                  sx={{ mr: 1, color: 'primary.dark' }}
                >
                  Dashboard
                </Button>
                <Button
                  onClick={() => router.visit('/vendors')}
                  sx={{ mr: 1, color: 'primary.dark' }}
                >
                  My Vendors
                </Button>
                {current_user.admin && (
                  <Button
                    onClick={() => router.visit('/admin/categories')}
                    sx={{ mr: 1, color: 'primary.dark' }}
                  >
                    Admin
                  </Button>
                )}
                <IconButton onClick={handleMenuOpen} sx={{ color: 'primary.dark' }}>
                  <AccountCircleIcon />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem disabled>
                    {current_user.email}
                  </MenuItem>
                  <MenuItem onClick={handleSignOut}>Sign Out</MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  onClick={() => router.visit('/users/sign_in')}
                  sx={{ mr: 1, color: 'primary.dark' }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  onClick={() => router.visit('/users/sign_up')}
                  sx={{ borderRadius: 5 }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1, py: noContainer ? 0 : 4 }}>
        {noContainer ? (
          <>
            {title && (
              <Container maxWidth="lg">
                <Typography variant="h4" component="h1" gutterBottom>
                  {title}
                </Typography>
              </Container>
            )}
            {children}
          </>
        ) : (
          <Container maxWidth="lg">
            {title && (
              <Typography variant="h4" component="h1" gutterBottom>
                {title}
              </Typography>
            )}
            {children}
          </Container>
        )}
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: '#F5E6E0',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            Okonomi App Store
          </Typography>
        </Container>
      </Box>

      {flash.notice && (
        <Snackbar open={flashOpen} autoHideDuration={6000} onClose={() => setFlashOpen(false)}>
          <Alert severity="success" onClose={() => setFlashOpen(false)}>
            {flash.notice}
          </Alert>
        </Snackbar>
      )}
      {flash.alert && (
        <Snackbar open={flashOpen} autoHideDuration={6000} onClose={() => setFlashOpen(false)}>
          <Alert severity="error" onClose={() => setFlashOpen(false)}>
            {flash.alert}
          </Alert>
        </Snackbar>
      )}
    </Box>
  )
}
